/**
 * TypeScript type definitions matching backend GraphQL schema.
 */

// ============== Task Enums ==============

export enum TaskStatus {
  PENDING = "PENDING",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  REJECTED = "REJECTED", // congrid
  CANNOT_MEASURE = "CANNOT_MEASURE", // congrid
  NOT_APPLICABLE = "NOT_APPLICABLE", // congrid
  PROBLEM = "PROBLEM", // combination of rejected/cannot-measure for congrid parent tasks.
  UNKNOWN = "UNKNOWN", // Used when status is missing/invalid, to avoid breaking the UI.
}

export enum TaskType {
  TEMPLATE_MAIN_TASK = "TEMPLATE_MAIN_TASK",
  TEMPLATE_SUBTASK = "TEMPLATE_SUBTASK",
  INTERNAL_MAIN_TASK = "INTERNAL_MAIN_TASK",
  INTERNAL_SUBTASK = "INTERNAL_SUBTASK",
  INSPECTION_MAIN_TASK = "INSPECTION_MAIN_TASK",
  INSPECTION_SUBTASK = "INSPECTION_SUBTASK",
  DOCUMENT_MAIN_TASK = "DOCUMENT_MAIN_TASK",
  DOCUMENT_SUBTASK = "DOCUMENT_SUBTASK",
}

// ============== Project Enums ==============

export enum ProjectStatus {
  TENDERING = "TENDERING",
  LOST = "LOST",
  PLANNING = "PLANNING",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  WARRANTY = "WARRANTY",
  ARCHIVED = "ARCHIVED",
}

export enum ProjectType {
  KVR = "KVR",
  KOKONAISURAKKA = "KOKONAISURAKKA",
  PROJEKTIJOHTO = "PROJEKTIJOHTO",
}

// ============== Location Enum ==============

export enum LocationType {
  BUILDING = "BUILDING",
  SECTION = "SECTION",
  FLOOR = "FLOOR",
  ZONE = "ZONE",
  SPACE = "SPACE",
}

// ============== Task Template Enums ==============

export enum TaskAreaType {
  CONSTRUCTION = "CONSTRUCTION",
  PROCUREMENT = "PROCUREMENT",
  WARRANTY = "WARRANTY",
}

// ============== Types ==============

export type UserAccessLevel = 'ADMIN' | 'INTERNAL_PLUS' | 'INTERNAL' | 'GUEST';

export interface User {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  isActive: boolean;
  accessLevel: UserAccessLevel;
  createdAt: string;
  lastLoginAt?: string | null;
  azureObjectId?: string | null;
  congridUserId?: string | null;
  sitedriveUserId?: string | null;
  /** Id of the final active substitute (flattened nightly); set → substituted. */
  substituteUserId?: string | null;
  /** Name of the active substitute, pulled along with the user (no nested
   * resolver). When set, render the responsible person as "<substitute>
   * (<responsible>)". */
  substituteFirstName?: string | null;
  substituteLastName?: string | null;
}

export interface Substitution {
  id: string;
  substitutedUserId: string;
  substituteUserId: string;
  startDate: string;
  endDate: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  substitutedUser?: User | null;
  substituteUser?: User | null;
}

export interface Project {
  id: string;
  name: string;
  description?: string | null;
  status: ProjectStatus;
  projectType: ProjectType;
  startDate?: string | null;
  objectiveEndDate?: string | null;
  externalQualityInspectionPlatformProjectId?: string | null;
  externalSchedulingPlatformProjectId?: string | null;
  effectiveCongridProjectId?: string | null;
  // Server-resolved project-scoped capabilities for the requesting user, read
  // from the same table the mutation gates enforce. Read via
  // `hasCapability()` in `@/lib/projectCapabilities` — never recompute from
  // accessLevel + project roles. Absent when a query does not select it.
  viewerCapabilities?: string[] | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface Section {
  id: string;
  name: string;
  code?: string | null;
  procurementPackageId?: string | null;
  templateWorkSectionId?: string | null;
  subcontractorId?: string | null;
  plannedStart?: string | null;
  plannedEnd?: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  procurementPackage?: ProcurementPackage | null;
}

export interface ProcurementPackage {
  id: string;
  templateProcurementPackageId: string;
  responsibleUserId: string;
  plotId?: string | null;
  code: string;
  name?: string | null;
  plannedStart?: string | null;
  plannedEnd?: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface Location {
  id: string;
  name: string;
  description?: string | null;
  locationType: LocationType;
  area?: number | null;
  projectId: string;
  parentLocationId?: string | null;
  schedulingPlatformSpaceId?: string | null;
  schedulingPlatformSpaceParentId?: string | null;
  inspectionTargetId?: string | null;
  inspectionParentTargetId?: string | null;
  hierarchyPath?: string[] | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export type RecurrenceFrequency =
  | "DAILY_WEEKDAYS"
  | "WEEKLY"
  | "MONTHLY_NTH_WEEKDAY";

export interface TaskRecurrenceSeries {
  id: string;
  frequency: RecurrenceFrequency;
  intervalCount: number;
  weekday?: number | null;
  weekOfMonth?: number | null;
  startDate: string;
  endDate: string;
  rootSeriesId: string;
  previousSeriesId?: string | null;
  version: number;
}

export interface Task {
  id: string;
  title: string;
  description?: string | null;
  status: TaskStatus;
  taskType?: TaskType | null;
  parentId?: string | null;
  projectId: string;
  sectionId?: string | null;
  procurementPackageId?: string | null;
  plotId?: string | null;
  locationId?: string | null;
  templateTaskId?: string | null;
  subcontractorId?: string | null;
  externalSchedulingPlatformTaskId?: string | null;
  externalInspectionPlatformInspectionId?: string | null;
  congridInspectionUrl?: string | null;
  createdByUserId?: string | null;
  taskCommentId?: string | null;
  syncLogId?: string | null;
  dueDate?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  externalOrder?: string | null;
  scheduledStart?: string | null;
  scheduledEnd?: string | null;
  responsibleUserId?: string | null;
  // Recurrence. `occurrenceDate` is the sarja's own date for this task and is
  // fixed at generation; `dueDate` is the määräaika the user may move, so the
  // two diverge as soon as anyone drags a deadline.
  recurrenceSeriesId?: string | null;
  occurrenceDate?: string | null;
  isRecurring?: boolean | null;
  isOffSchedule?: boolean | null;
  recurrenceSeries?: TaskRecurrenceSeries | null;
  // Server-computed UI hints, resolved from backend/app/core/access_policy.py
  // by the same predicates the mutation gates enforce. Never reimplement the
  // role/capability logic client-side — read these instead.
  //
  // `viewerCanWrite` mirrors `requires_task_write`, which gates BOTH updateTask
  // and deleteTask, so it governs Muokkaa, Poista and the status control.
  viewerCanWrite?: boolean | null;
  // `viewerCanAddSubtask` mirrors `RequiresTaskCreate` with this task as parent.
  viewerCanAddSubtask?: boolean | null;
  /** @deprecated Use `viewerCanWrite` — identical predicate, clearer name. */
  viewerCanEditStatus?: boolean | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  lastSyncedAt?: string | null;
}

export interface TaskComment {
  id: string;
  content: string;
  taskId: string;
  writerId: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  writer: User | null;
}

export interface Subcontractor {
  id: string;
  name: string;
  yTunnus?: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface Contact {
  id: string;
  name: string;
  title?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  notes?: string | null;
  subcontractorId: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface Plot {
  id: string;
  projectId: string;
  name: string;
  schedulingPlatformLocationId?: string | null;
  inspectionPlatformQualityMatrixId?: string | null;
  inspectionPlatformProjectId?: string | null;
  version?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface TaskStats {
  total: number;
  completed: number;
  late: number;
}

export interface HeadsectionStats {
  headsectionNumber: number;
  label: string;
  total: number;
  completed: number;
  pending: number;
  inProgress: number;
  problem: number;
  late: number;
  completedPercent: number;
  pendingPercent: number;
  inProgressPercent: number;
  problemPercent: number;
  latePercent: number;
}

export interface ProcurementPackageDetailed extends ProcurementPackage {
  sections?: Section[];
  responsibleUser?: User | null;
  taskStats?: TaskStats;
}

export interface PlotDetailed extends Plot {
  procurementPackages?: ProcurementPackageDetailed[];
  locations?: Location[];
}

export interface PlotWithStats extends Plot {
  headsectionStats?: HeadsectionStats[];
}

export interface ProjectWithPlots extends Project {
  plots?: PlotWithStats[];
  locations?: Location[];
  tyopaallikko?: User | null;
  vastaavaTyonjohtaja?: User | null;
}

export interface SubtaskProgress {
  pending: number;
  inProgress: number;
  completed: number;
  problem: number;
  total: number;
}

/** Originating Sitedrive work-package context (inspection tasks). */
export interface TaskSitedriveMetadata {
  sitedriveWorkPackage?: string | null;
  sitedriveParentWorkPackage?: string | null;
  sitedriveGrandparentWorkPackage?: string | null;
}

export interface TaskExtended extends Task {
  comments?: TaskComment[] | null;
  responsibleUser?: User | null;
  /**
   * Who the task falls back to if its `responsibleUserId` override is dropped.
   * Null means nothing resolves, so the override cannot be dropped. Resolved by
   * a per-task chain walk — only selected in single-task queries.
   */
  inheritedResponsibleUser?: User | null;
  project?: Project | null;
  section?: Section | null;
  plot?: Plot | null;
  location?: Location | null;
  children?: TaskExtended[] | null;
  procurementPackage?: ProcurementPackage | null;
  subtaskProgress?: SubtaskProgress | null;
  sitedriveMetadata?: TaskSitedriveMetadata | null;
}

export interface TaskFilter {
  id?: string;
  title?: string;
  status?: TaskStatus;
  taskType?: TaskType;
  parentId?: string;
  projectId?: string;
  sectionId?: string;
  locationId?: string;
  subcontractorId?: string;
  dueDate?: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  responsibleUserId?: string;
}

export interface TaskUpdateInput {
  id: string;
  title?: string;
  description?: string;
  status?: TaskStatus;
  taskType?: TaskType;
  parentId?: string;
  subcontractorId?: string;
  externalSchedulingPlatformTaskId?: string;
  externalInspectionPlatformInspectionId?: string;
  taskCommentId?: string;
  syncLogId?: string;
  projectId?: string;
  sectionId?: string;
  locationId?: string;
  dueDate?: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  responsibleUserId?: string;
}

export interface TaskCreateInput {
  title: string;
  description?: string;
  status?: TaskStatus;
  taskType?: TaskType;
  parentId?: string;
  sectionId?: string;
  procurementPackageId?: string;
  plotId?: string;
  templateTaskId?: string;
  subcontractorId?: string;
  externalSchedulingPlatformTaskId?: string;
  externalInspectionPlatformInspectionId?: string;
  createdByUserId?: string;
  taskCommentId?: string;
  syncLogId?: string;
  dueDate?: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  responsibleUserId?: string;
}

// ============== Connection Types ==============

export interface TaskConnection {
  items: Task[];
  totalCount: number;
  hasNextPage: boolean;
  endCursor: string | null;
}

export interface ProjectConnection {
  items: Project[];
  totalCount: number;
  hasNextPage: boolean;
}

export interface SectionConnection {
  items: Section[];
  totalCount: number;
  hasNextPage: boolean;
}

export interface UserConnection {
  items: User[];
  totalCount: number;
  hasNextPage: boolean;
}

/* ── Kehitys — feedback board. Mirrors backend/app/models/feedback_*.py ─── */

export type FeedbackKind = "BUG" | "IDEA";

export type FeedbackStatus =
  | "NEW"
  | "TRIAGED"
  | "IN_PROGRESS"
  | "DONE"
  | "WONT_DO";

export type FeedbackMediaType = "IMAGE" | "VIDEO";

export interface FeedbackAttachment {
  id: string;
  /** Branch on this, never on contentType. */
  mediaType: FeedbackMediaType;
  filename: string;
  /** kuvateksti — what this image or clip shows. Not the report's description. */
  caption: string | null;
  contentType: string;
  sizeBytes: number;
  /** Frame dimensions. 0 means unknown — do not reserve space. */
  width: number;
  height: number;
  /** VIDEO only; null for images. Rendered as a 0:34 badge. */
  durationSeconds: number | null;
  /**
   * Path on our own API, not a signed blob URL and **not an element src** —
   * fetching it with a bearer token mints the signed URL. Use
   * `useAttachmentUrl`, which does that and returns something an <img> or
   * <video> can load.
   */
  url: string;
  /**
   * Mint path for the image thumbnail, or a video's poster frame. **Null** when
   * no preview exists — a video whose codec the browser could not decode at
   * upload time. Render a placeholder rather than an <img> with an empty src.
   */
  thumbUrl: string | null;
  createdAt: string;
}

export interface FeedbackComment {
  id: string;
  reportId: string;
  body: string;
  createdById: string;
  createdBy?: Pick<
    User,
    "id" | "firstName" | "lastName" | "email" | "accessLevel"
  > | null;
  attachments: FeedbackAttachment[];
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface FeedbackReportContext {
  path?: string;
  browser?: string;
  os?: string;
  appVersion?: string;
}

export interface FeedbackReport {
  id: string;
  reference: string;
  kind: FeedbackKind;
  status: FeedbackStatus;
  title: string;
  description: string | null;
  voteCount: number;
  commentCount: number;
  createdById: string;
  createdBy?: Pick<User, "id" | "firstName" | "lastName" | "email"> | null;
  projectId: string | null;
  context: FeedbackReportContext | null;
  resolutionNote: string | null;
  viewerHasVoted: boolean;
  viewerCanEdit: boolean;
  viewerCanDelete: boolean;
  attachments: FeedbackAttachment[];
  comments?: FeedbackComment[];
  version: number;
  createdAt: string;
  updatedAt: string;
}
